import { MealTime } from "@/clients/models/MealTime";
import { PlanPageContainer } from "@/components/container/plan-page-container";
import { PageTitleComponent } from "@/components/page-title";
import { MealTimeSegmentButtons } from "@/components/segment-buttons/meal-time-segment-buttons";
import { MealPreferenceList } from "@/features/meal-plan/components/meal-preference-list";
import { useGetDislikedMeals } from "@/features/meal-plan/hooks/use-get-disliked-meals";
import { useGetFavouriteMeals } from "@/features/meal-plan/hooks/use-get-favourite-meals";
import { cn } from "@/utils/cn";
import { useState } from "react";

export default function MealPreferencesPage() {
  const [activeTab, setActiveTab] = useState<"liked" | "disliked">("liked");
  const [mealTime, setMealTime] = useState<MealTime>(MealTime.BREAKFAST);
  const { favouriteMealsByMealTime, isFavouriteLoading } =
    useGetFavouriteMeals();
  const { dislikedMeals, isDislikedMealsLoading } = useGetDislikedMeals();
  const selectedFavouriteMeals = favouriteMealsByMealTime?.[mealTime] ?? [];

  const isLikedTab = activeTab === "liked";

  return (
    <PlanPageContainer
      bgColor="background-white"
      className="p-4 pb-20 gap-6 overflow-auto"
    >
      <PageTitleComponent
        title="Meal preferences"
        description="Add favourites you want to keep and remove meals you never want to see again."
      />

      <div className="w-full flex gap-3">
        {[
          { value: "liked", label: "Favourites" },
          { value: "disliked", label: "Disliked" },
        ].map((tab) => {
          const isActive = activeTab === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => setActiveTab(tab.value as "liked" | "disliked")}
              className={cn(
                "flex-1 rounded-2xl border px-4 py-3 text-sm font-semibold transition-colors",
                isActive
                  ? "bg-black text-white border-black"
                  : "bg-white text-gray-500 border-gray-200",
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {isLikedTab ? (
        <section className="w-full flex flex-col gap-4">
          <MealTimeSegmentButtons
            selected={mealTime}
            onSelect={(value) => setMealTime(value)}
          />
          <MealPreferenceList
            meals={selectedFavouriteMeals}
            emptyMessage={`No favourites saved for ${mealTime.toLowerCase()} yet.`}
            isLoading={isFavouriteLoading}
            variant="like"
            mealTime={mealTime}
          />
        </section>
      ) : (
        <section className="w-full flex flex-col gap-4">
          <MealPreferenceList
            meals={dislikedMeals}
            emptyMessage="You have not disliked any meals yet."
            isLoading={isDislikedMealsLoading}
            variant="dislike"
          />
        </section>
      )}
    </PlanPageContainer>
  );
}
